import React from 'react';
import PropTypes from 'prop-types';
import {
  FormGroup,
  Row,
  Col,
  ControlLabel,
  Button,
  ButtonToolbar,
  Tooltip,
  OverlayTrigger,
  Glyphicon,
} from 'react-bootstrap';

import { Field, reduxForm, formValueSelector, formPropTypes, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { compose, lifecycle } from 'recompose';
import _ from 'lodash';

import DepartmentInput from '../../../fields/DepartmentInput';
import DistrictInput from '../../../fields/DistrictInput';
import TicketInputEnhanced from '../../../fields/TicketInput/containers/TicketInputEnhanced';
import OperatorInput from '../../../fields/OperatorInput';
import WellInput from '../../../fields/WellInput';
import LeaseInput from '../../../fields/LeaseInput';
import EquipmentPicker from '../../../fields/EquipmentPicker';
import JobInput from '../../../fields/JobInput';
import CustomerFeedbackListInput from '../../../fields/CustomerFeedbackInput';

import { enableLookupFeature } from '../../../configuration/selectors';
import { BaseField, DollarField, NumberField, DateField } from '../../../fields';
import '../../../../components/App.css';
import { maybeId } from '../../../../utils';

import Overlay from '../../../../components/Overlay';


export const WELL_SERVICE = 'Well Service';
export const TRUCK = 'Truck';
const WIRELINE = 'Wireline';
const ACID = 'Acid';
const CEMENT = 'Cement';
const FRAC = 'Frac';


export const FORM_NAME = 'ticketEntryForm';


const validate = (values, props) => {
  const errors = {};

  if (!values.date) {
    errors.date = 'Required';
  }
  if (!values.department) {
    errors.department = 'Required';
  }
  if (!values.district) {
    errors.district = 'Required';
  }
  if (values.equipment.length === 0) {
    errors.equipment = 'Required';
  }
  if (!values.ticket_number) {
    errors.ticket_number = 'Required';
  }
  if (!values.operator) {
    errors.operator = 'Required';
  }
  if (!values.lease) {
    errors.lease = 'Required';
  }
  if (!values.well) {
    errors.well = 'Required';
  }
  if (Number.isNaN(values.billing_revenue)
      || values.billing_revenue === null
      || values.billing_revenue === undefined) {
    errors.billing_revenue = 'Required';
  }
  return errors;
};

export const jobEditMessage = 'To edit the opeartor/lease for a job go to the ticket viewer for a ticket on this job and click "edit job"';

/* eslint-disable */
const createRenderer = (render) => ({ input, meta, label, ...rest }) => {
  return (
    <div
      className={[
        meta.error && meta.touched ? 'form-error' : '',
        meta.active ? 'form-active' : '',
      ].join(' ')}
    >
      <ControlLabel>
        {label}
      </ControlLabel>

      {render(input, label, rest)}

      {meta.error && meta.touched &&
      <span>
        {meta.error}
      </span>
      }
    </div>
  );
};
/* eslint-enable */
const RenderDepartment = createRenderer((input, label, { disabled, districtId }) => {
  return (
    <DepartmentInput districtId={districtId} disabled={disabled} {...input} />
  );
});

const RenderDistrict = createRenderer((input, label, { disabled, departmentId }) => {
  return (
    <DistrictInput departmentId={departmentId} disabled={disabled} {...input} />
  );
});

const RenderTicket = createRenderer((input, label, { disabled }) => {
  return (
    <TicketInputEnhanced disabled={disabled} assigned="unassigned" {...input} />
  );
});

const RenderOperator = createRenderer((input, label, { disabled, enableLookup, hasJob }) => {
  return (
    <Overlay enabled={hasJob} message={jobEditMessage}>
      <OperatorInput
        lookupJoinType="user_records"
        enableLookup={enableLookup}
        disabled={disabled}
        {...input}
      />
    </Overlay>
  );
});

const RenderWell = createRenderer((input, label, { disabled, lease, lookupJoinType }) => {
  return (
    <WellInput
      lease={lease}
      disabled={disabled}
      lookupJoinType={lookupJoinType}
      {...input}
    />
  );
});

const RenderLease = createRenderer((input, label, { disabled, operator, lookupJoinType, hasJob }) => {
  return (
    <Overlay enabled={hasJob} message={jobEditMessage}>
      <LeaseInput
        operator={operator}
        disabled={disabled}
        lookupJoinType={lookupJoinType}
        {...input}
      />
    </Overlay>
  );
});

const RenderEquipment = createRenderer((input, label, { disabled }) => {
  return (
    <EquipmentPicker disabled={disabled} {...input} />
  );
});

const RenderJobInput = createRenderer((input, label, { disabled }) => {
  return (
    <JobInput disabled={disabled} {...input} />
  );
});

const RenderCustomerFeedbackInput = createRenderer((input, label, { disabled }) => {
  return (
    <CustomerFeedbackListInput disabled={disabled} {...input} />
  );
});

/*
* We have to use the next-line version of disable here because of all the other enabling and
* disabling happening in this file.
 */

/* eslint-disable react/forbid-prop-types, react/require-default-props */
const propTypes = {
  closeTicketEntryForm: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
  departmentObject: PropTypes.object,
  leaseObject: PropTypes.object,
  jobId: PropTypes.number.isRequired,
  operatorObject: PropTypes.object,
  lookupJoinType: PropTypes.oneOf(['user_records', 'lookup_records', 'union']),
  enableLookup: PropTypes.bool.isRequired,
  ...formPropTypes,
};
/* eslint-enable */

const TicketEntry = (props) => {
  const {
    closeTicketEntryForm,
    handleSubmit,
    initialValues,
    submitting,
    pristine,
    jobId,
    departmentObject,
    dispatchCreateUpdateTicket,
    leaseObject,
    operatorObject,
    invalid,
    districtId,
    departmentId,
    error,
    lookupJoinType,
    enableLookup,
    jobObject,
  } = props;

  const { canEdit } = props;
  const isNewRecord = !(initialValues.id);

  const mutable = (canEdit);

  const handleShowBillableHours = (departmentObj) => {
    let result = false;
    if (departmentObj) {
      result = (departmentObj.name === WELL_SERVICE || departmentObj.name === TRUCK);
    }

    return result;
  };

  const handleShowCustomerFeedback = (departmentObj) => {
    let result = false;
    if (departmentObj) {
      result = (
        departmentObj.name === ACID
        || departmentObj.name === WIRELINE
        || departmentObj.name === FRAC
        || departmentObj.name === CEMENT
      );
    }

    return result;
  };

  const tooltip = (
    <Tooltip id="tooltip" placement="top">
      <strong>This ticket can not be changed.</strong> Ticket has already been processed.
    </Tooltip>
  );

  const onSubmit = async (values) => {
    const ticketObject = Object.assign({}, values);
    if (!values.supplemental_info) { ticketObject.supplemental_info = ''; }

    ticketObject.recommended_feedback = (ticketObject.recommended_feedback)
      ? parseInt(values.recommended_feedback.value, 10) : null;

    const ticketInputObject = _.omit(ticketObject, ['department.has_hourly_billing', 'immutable_reason', 'mutable']);

    try {
      await dispatchCreateUpdateTicket(ticketInputObject, jobId);
    } catch (e) {
      const message = e.graphQLErrors.reduce((errorMessage, gqlError) => {
        return errorMessage + gqlError.details;
      }, '');
      throw new SubmissionError({ _error: message });
    }
  };

  /* Figure disable logic */
  const hasJob = jobObject !== null;
  const disableWell = !leaseObject || !operatorObject;
  const disableLease = !operatorObject || hasJob;
  const disableOperator = hasJob;

  return (
    <div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormGroup>

          <Row>
            <Col xs={6}>
              <FormGroup>
                <DateField
                  name="date"
                  label="Date"
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  districtId={districtId}
                  name="department"
                  type="select"
                  label="Department"
                  format={null}
                  component={RenderDepartment}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  departmentId={departmentId}
                  name="district"
                  type="select"
                  label="District"
                  format={null}
                  component={RenderDistrict}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="equipment"
                  type="select"
                  label="Equipment"
                  format={null}
                  component={RenderEquipment}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="ticket_number"
                  type="input"
                  label="Ticket Number"
                  format={null}
                  component={RenderTicket}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="job"
                  type="select"
                  label="Job Number"
                  format={null}
                  component={RenderJobInput}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  name="operator"
                  type="select"
                  label="Operator"
                  format={null}
                  hasJob={hasJob}
                  disabled={disableOperator}
                  component={RenderOperator}
                  enableLookup={enableLookup}
                />
              </FormGroup>
            </Col>
            <Col xs={6}>
              <FormGroup>
                <Field
                  operator={operatorObject}
                  name="lease"
                  type="select"
                  label="Lease"
                  format={null}
                  component={RenderLease}
                  hasJob={hasJob}
                  disabled={disableLease}
                  lookupJoinType={lookupJoinType}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <FormGroup>
                <Field
                  lease={leaseObject}
                  name="well"
                  type="select"
                  label="Well"
                  format={null}
                  component={RenderWell}
                  disabled={disableWell}
                  lookupJoinType={lookupJoinType}
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FormGroup>
                <BaseField
                  name="supplemental_info"
                  componentClass="textarea"
                  label="Supplemental Information"
                />
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              {handleShowBillableHours(departmentObject)
              && (
              <FormGroup>
                <NumberField
                  name="billing_hours"
                  label="Billing Hours"
                />
              </FormGroup>
              )}
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FormGroup>
                <DollarField
                  name="billing_revenue"
                  label="Net Revenue"
                />
              </FormGroup>
            </Col>
          </Row>
          {handleShowCustomerFeedback(departmentObject) && (
            <Row>
              <Col xs={12}>
                <FormGroup>
                  <Field
                    name="recommended_feedback"
                    type="select"
                    label="Would They Recommend Us?"
                    format={null}
                    component={RenderCustomerFeedbackInput}
                  />
                </FormGroup>
              </Col>
            </Row>
          )}
        </FormGroup>
        <Row>
          <Col md={12} className="pull-right">
            {!mutable && (
              <div className="pull-right">
                <OverlayTrigger placement="top" overlay={tooltip}>
                  <Glyphicon className="glyphicon glyphicon-question-sign" />
                </OverlayTrigger>
              </div>
            )}
          </Col>
        </Row>
        <Row>
          <Col md={12}>
            <ButtonToolbar className="pull-right">
              <Button bsStyle="warning" onClick={() => { closeTicketEntryForm(); }}>
                Cancel
              </Button>

              <Button
                bsStyle="primary"
                type="submit"
                disabled={!mutable || (submitting || pristine || invalid)}
              >
                {isNewRecord && 'Create'}
                {!isNewRecord && 'Update'}
              </Button>

            </ButtonToolbar>
          </Col>
        </Row>
        {error && (
          <span>
            <strong>{error}</strong>
          </span>
        )}
      </form>
    </div>
  );
};


TicketEntry.propTypes = propTypes;
TicketEntry.defaultProps = {
  initialValues: {},
};

const withForm = reduxForm({
  form: FORM_NAME,
  enableReinitialize: true,
  validate,
});

const selector = formValueSelector(FORM_NAME);

const withRedux = connect((state) => {
  const jobObject = selector(state, 'job');
  const departmentObject = selector(state, 'department');
  const operatorObject = selector(state, 'operator');
  const leaseObject = selector(state, 'lease');
  const district = selector(state, 'district');
  const districtId = maybeId(district);
  const departmentId = maybeId(departmentObject);
  const lookupJoinType = enableLookupFeature(state) ? 'union' : 'user_records';
  const enableLookup = enableLookupFeature(state);

  return {
    departmentObject,
    operatorObject,
    leaseObject,
    districtId,
    departmentId,
    lookupJoinType,
    enableLookup,
    jobObject,
  };
});

const checkIfObjectChanged = (prevObject, nextObject) => {
  if (prevObject !== nextObject) {
    return true;
  } if (prevObject && !nextObject) {
    return true;
  }
  return false;
};

const handleFieldChangeRelationship = lifecycle({
  componentWillReceiveProps(nextProps) {
    const {
      operatorObject: nextOperatorObject,
      leaseObject: nextLeaseObject,
      jobObject: nextJobObject,
    } = nextProps;

    const {
      jobObject: prevJobObject,
    } = this.props;
    const didJobChange = checkIfObjectChanged(prevJobObject, nextJobObject);
    /*
      What is going on here? First of all, how do we want the forum to act?
      When the job for a ticket is updated we want to select the new lease and
      operator for the selected job. If the job was cleared (the x in the UI
      was pressed) we want to clear out the operator and lease. If there is no
      job selected we want to be able to clear the operator or lease and have
      the change cascade down through the well.
    */
    if (didJobChange) {
      /*
        When the job is set to null we update the operator to null. The change
        cause this method to be hit again, setting lease to null. Then this
        method will be called yet again setting well to null
      */
      if (nextJobObject === null) {
        this.props.change('operator', null);
      } else {
        const { lookup: opLookup } = nextJobObject.lease.operator;
        const { lookup: leLookup } = nextJobObject.lease;
        /*
          The operator change causes this method to be called again, setting
          lease to null. The 2nd change call here updates the lease. This method
          is called a third time, and the last if below is hit. Since
          lease operator id equals operator id lease is not set to null.
        */
        this.props.change('operator', opLookup);
        this.props.change('lease', leLookup);
        this.props.change('well', null);
      }
    } else {
      // lease is null so set the well as null.
      if (nextLeaseObject === null) {
        this.props.change('well', null);
      }
      // operator is null so set lease to null. This will then cause well to be
      // set to null the 2nd time this callback is called.
      if (nextOperatorObject === null) {
        this.props.change('lease', null);
      }
      // If we have an operator and lease the lease's operator MUST match the
      // currently selected operator. If it doesn't reset the lease
      if (nextLeaseObject !== null && nextOperatorObject !== null) {
        if (nextOperatorObject.user_id !== nextLeaseObject.lease.operator.id) {
          this.props.change('lease', null);
        }
      }
    }
  },
});


const enhance = compose(
  withForm,
  withRedux,
  handleFieldChangeRelationship,
);


export default enhance(TicketEntry);
